import * as THREE from 'three';

function hash2(x: number, y: number, seed: number) {
  let h = (x * 374761393 + y * 668265263 + seed * 2147483647) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

function periodicValueNoise(x: number, y: number, period: number, seed: number) {
  const xi = Math.floor(x), yi = Math.floor(y);
  const xf = x - xi, yf = y - yi;
  const u = xf * xf * (3 - 2 * xf), v = yf * yf * (3 - 2 * yf);
  const p = (a: number) => ((a % period) + period) % period;
  const a = hash2(p(xi), p(yi), seed), b = hash2(p(xi + 1), p(yi), seed);
  const c = hash2(p(xi), p(yi + 1), seed), d = hash2(p(xi + 1), p(yi + 1), seed);
  return (a * (1 - u) + b * u) * (1 - v) + (c * (1 - u) + d * u) * v;
}

function fbm(x: number, y: number, basePeriod: number, oct: number, seed: number) {
  let s = 0, a = 0.5, f = 1, n = 0;
  for (let i = 0; i < oct; i++) {
    s += a * periodicValueNoise(x * f, y * f, basePeriod * f, seed + i * 17);
    n += a; a *= 0.5; f *= 2;
  }
  return s / n;
}

function worley(x: number, y: number, period: number, seed: number) {
  const xi = Math.floor(x), yi = Math.floor(y);
  let d1 = 9, d2 = 9;
  for (let j = -1; j <= 1; j++) for (let i = -1; i <= 1; i++) {
    const cx = xi + i, cy = yi + j;
    const px = ((cx % period) + period) % period, py = ((cy % period) + period) % period;
    const fx = cx + hash2(px, py, seed), fy = cy + hash2(px, py, seed + 99);
    const d = Math.hypot(fx - x, fy - y);
    if (d < d1) { d2 = d1; d1 = d; } else if (d < d2) d2 = d;
  }
  return [d1, d2];
}

/**
 * Tileable 512x512 detail noise: R fbm (period 8 cells), G worley pebbles (F2-F1 edges in B), A fine grain.
 */
export function buildDetailNoise(size = 512): THREE.DataTexture {
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const u = x / size, v = y / size;
      const n1 = fbm(u * 8, v * 8, 8, 5, 1);
      const [w1, w2] = worley(u * 24, v * 24, 24, 5);
      const g = Math.min(1, w1 * 1.25);
      const edge = Math.min(1, (w2 - w1) * 3.0);
      const grain = fbm(u * 64, v * 64, 64, 2, 9);
      const i = (y * size + x) * 4;
      data[i] = Math.round(n1 * 255);
      data[i + 1] = Math.round(g * 255);
      data[i + 2] = Math.round(edge * 255);
      data[i + 3] = Math.round(grain * 255);
    }
  }
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat, THREE.UnsignedByteType);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = true;
  tex.anisotropy = 8;
  tex.needsUpdate = true;
  return tex;
}

/** Tileable cloud weather map: R/G coverage noise at two scales, B cloud-type variation. */
export function buildWeatherMap(size = 512): THREE.DataTexture {
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const u = x / size, v = y / size;
      const f = fbm(u * 6, v * 6, 6, 5, 31);
      const [w1] = worley(u * 10, v * 10, 10, 77);
      const cov = Math.min(1, Math.max(0, f * 0.75 + (1 - Math.min(1, w1)) * 0.45 - 0.12));
      const g = fbm(u * 16, v * 16, 16, 4, 51);
      const b = fbm(u * 3, v * 3, 3, 3, 91);
      const i = (y * size + x) * 4;
      data[i] = Math.round(cov * 255);
      data[i + 1] = Math.round(g * 255);
      data[i + 2] = Math.round(b * 255);
      data[i + 3] = 255;
    }
  }
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat, THREE.UnsignedByteType);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = true;
  tex.needsUpdate = true;
  return tex;
}
